import { useRef, useState, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { getCurrentTimestamp } from '../utils';
import type { SignatureCaptureState, SignatureMode } from '../types';

type SignatureCaptureData = {
  dataUrl: string;
  timestamp: string;
};

type UseSignatureCaptureResult = {
  canvasRef: React.RefObject<SignatureCanvas>;
  state: SignatureCaptureState;
  mode: SignatureMode;
  setMode: (mode: SignatureMode) => void;
  handleClear: () => void;
  handleDone: () => SignatureCaptureData | null;
  handleEdit: (clearData?: boolean) => void;
  checkIsEmpty: () => boolean;
  setTypedSignature: (dataUrl: string | null) => void;
  confirmTypedSignature: () => SignatureCaptureData | null;
  loadExistingSignature: (dataUrl: string, timestamp: string) => void;
};

export const useSignatureCapture = (): UseSignatureCaptureResult => {
  const canvasRef = useRef<SignatureCanvas>(null);
  const [mode, setModeInternal] = useState<SignatureMode>('draw');

  const [state, setState] = useState<SignatureCaptureState>({
    isEmpty: true,
    isConfirmed: false,
    dataUrl: null,
    timestamp: null
  });

  const checkIsEmpty = useCallback((): boolean => {
    return canvasRef.current?.isEmpty() ?? true;
  }, []);

  const handleClear = useCallback(() => {
    canvasRef.current?.clear();
    setState({
      isEmpty: true,
      isConfirmed: false,
      dataUrl: null,
      timestamp: null
    });
  }, []);

  const handleDone = useCallback((): SignatureCaptureData | null => {
    if (canvasRef.current && !canvasRef.current.isEmpty()) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const timestamp = getCurrentTimestamp();

      setState({
        isEmpty: false,
        isConfirmed: true,
        dataUrl,
        timestamp
      });

      return { dataUrl, timestamp };
    }
    return null;
  }, []);

  const handleEdit = useCallback((clearData = true) => {
    setState(prev => ({
      ...prev,
      isConfirmed: false,
      dataUrl: clearData ? null : prev.dataUrl,
      timestamp: clearData ? null : prev.timestamp
    }));
  }, []);

  const setMode = useCallback((newMode: SignatureMode) => {
    setModeInternal(newMode);
    setState({
      isEmpty: true,
      isConfirmed: false,
      dataUrl: null,
      timestamp: null
    });
    if (canvasRef.current) {
      canvasRef.current.clear();
    }
  }, []);

  const setTypedSignature = useCallback((dataUrl: string | null) => {
    if (dataUrl) {
      setState(prev => ({
        isEmpty: false,
        isConfirmed: prev.isConfirmed,
        dataUrl,
        timestamp: prev.timestamp
      }));
    } else {
      setState({
        isEmpty: true,
        isConfirmed: false,
        dataUrl: null,
        timestamp: null
      });
    }
  }, []);

  const confirmTypedSignature = useCallback((): {
    dataUrl: string;
    timestamp: string;
  } | null => {
    if (state.dataUrl) {
      const timestamp = getCurrentTimestamp();
      setState(prev => ({
        ...prev,
        isConfirmed: true,
        timestamp
      }));
      return { dataUrl: state.dataUrl, timestamp };
    }
    return null;
  }, [state.dataUrl]);

  const loadExistingSignature = useCallback(
    (dataUrl: string, timestamp: string) => {
      setState({
        isEmpty: false,
        isConfirmed: true,
        dataUrl,
        timestamp
      });
    },
    []
  );

  return {
    canvasRef,
    state,
    mode,
    setMode,
    handleClear,
    handleDone,
    handleEdit,
    checkIsEmpty,
    setTypedSignature,
    confirmTypedSignature,
    loadExistingSignature
  };
};
